import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, CheckCircle2, Shield, Zap, Users, TrendingUp, Award, Sparkles, Smartphone, Star, Layers, Building2, Palette, Lightbulb, Truck, ShoppingBag, HeartPulse, BarChart3, Cog, Flag, Handshake, Mail, DollarSign, Headset, ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import HeroRotatingText from '../components/HeroRotatingText';
import DemoModal from '../components/DemoModal';
import HeroMedia from '../components/HeroMedia';

const Home = () => {
  const [demoOpen, setDemoOpen] = useState(false);

  // ============================================
  // White-Glove Excellence coverflow carousel
  // ============================================
  const whiteGloveCards = [
    {
      title: 'Hyper-Personalization',
      description:
        'Every workflow, integration, and rollout is built around how your business actually operates — not a template stretched to fit.',
      icon: Sparkles,
    },
    {
      title: 'Flawless Execution',
      description:
        'From system integration to daily operations, every detail is handled with the same precision your enterprise already demands of itself.',
      icon: Zap,
    },
    {
      title: 'Absolute Discretion',
      description:
        'Your data, your operations, your confidentiality — held to the highest standard, without exception.',
      icon: Shield,
    },
    {
      title: 'Concierge Access',
      description:
        'A direct line to the people building your solution. No tiers, no queues, no runaround.',
      icon: Users,
    },
  ];
  const [wgActive, setWgActive] = useState(0);
  const [wgIsMobile, setWgIsMobile] = useState(false);
  const [wgViewportWidth, setWgViewportWidth] = useState(1200);
  useEffect(() => {
    const update = () => {
      const w = typeof window !== 'undefined' ? window.innerWidth : 1200;
      setWgViewportWidth(w);
      setWgIsMobile(w < 768);
    };
    update();
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, []);
  const wgTotal = whiteGloveCards.length;
  const wgPrevIdx = (wgActive - 1 + wgTotal) % wgTotal;
  const wgNextIdx = (wgActive + 1) % wgTotal;
  const wgGoPrev = () => setWgActive((prev) => (prev - 1 + wgTotal) % wgTotal);
  const wgGoNext = () => setWgActive((prev) => (prev + 1) % wgTotal);
  const wgTouchStartX = useRef(null);
  const wgOnTouchStart = (e) => {
    wgTouchStartX.current = e.touches[0].clientX;
  };
  const wgOnTouchEnd = (e) => {
    if (wgTouchStartX.current == null) return;
    const dx = e.changedTouches[0].clientX - wgTouchStartX.current;
    if (Math.abs(dx) > 40) {
      if (dx > 0) wgGoPrev(); else wgGoNext();
    }
    wgTouchStartX.current = null;
  };

  const wgEasing = 'cubic-bezier(.22,.61,.36,1)';
  const wgTransition = `transform 0.6s ${wgEasing}, opacity 0.6s ${wgEasing}, box-shadow 0.6s ${wgEasing}, border-color 0.6s ${wgEasing}`;

  const wgCardWidth = wgIsMobile ? Math.min(420, Math.max(260, wgViewportWidth - 48)) : 420;
  const wgCardHeight = 390;
  const wgSideOffset = wgIsMobile ? Math.round(wgCardWidth * 0.92) : 390;

  const wgGetCardStyle = (idx) => {
    const base = {
      position: 'absolute',
      top: '50%',
      left: '50%',
      width: `${wgCardWidth}px`,
      height: `${wgCardHeight}px`,
      borderRadius: '18px',
      backgroundColor: '#25365A',
      padding: '40px 42px',
      transition: wgTransition,
      willChange: 'transform, opacity',
      cursor: idx === wgActive ? 'default' : 'pointer',
      boxSizing: 'border-box',
    };
    if (idx === wgActive) {
      return {
        ...base,
        transform: 'translate3d(-50%, -50%, 0) scale(1)',
        opacity: 1,
        zIndex: 3,
        boxShadow: '0 30px 60px rgba(20,30,48,0.32)',
        border: '1px solid rgba(212,175,55,0.6)',
      };
    }
    if (idx === wgPrevIdx) {
      return {
        ...base,
        transform: `translate3d(calc(-50% - ${wgSideOffset}px), -50%, 0) scale(0.88)`,
        opacity: 0.78,
        zIndex: 2,
        boxShadow: '0 14px 30px rgba(20,30,48,0.18)',
        border: '1px solid rgba(212,175,55,0.18)',
      };
    }
    if (idx === wgNextIdx) {
      return {
        ...base,
        transform: `translate3d(calc(-50% + ${wgSideOffset}px), -50%, 0) scale(0.88)`,
        opacity: 0.78,
        zIndex: 2,
        boxShadow: '0 14px 30px rgba(20,30,48,0.18)',
        border: '1px solid rgba(212,175,55,0.18)',
      };
    }
    // Hidden fourth card: directly behind center, scaled to 0.72, invisible.
    return {
      ...base,
      transform: 'translate3d(-50%, -50%, 0) scale(0.72)',
      opacity: 0,
      zIndex: 1,
      boxShadow: 'none',
      border: '1px solid rgba(212,175,55,0.18)',
      pointerEvents: 'none',
    };
  };

  const rotatingWords = ['teams', 'creators', 'operators', 'founders', 'visionaries'];

  const heroSideCards = [
    { title: 'Designed by Elegance', description: 'Design is how elegance becomes intelligence.', icon: Shield },
    { title: 'Powered by AI', description: "GoogLyn AI doesn't wait to be asked — it anticipates.", icon: Zap },
    { title: 'Accessible to All', description: 'We put people before code — and trust at the center of every interaction.', icon: Users },
  ];

  // ============================================
  // 🎬 HERO MEDIA SETTINGS
  // ============================================
  // Cloudflare Stream direct-MP4 URL — served via customer subdomain and
  // returns a 302 to a short-lived signed CDN URL (browser follows it
  // automatically). No iframe embed, no Allowed-Origins restriction to
  // worry about. To swap in a different clip: upload to Cloudflare Stream
  // and replace the 32-char video ID in the URL below.
  const heroSettings = {
    type: 'uploaded-video',
    videoUrl: 'https://customer-q87c1i5e2o40bvjt.cloudflarestream.com/e8f7b3b9c6220511f3a3c4dbfdcbcdbd/downloads/default.mp4',
    overlay: 0.4, // Darkness of overlay (0-1)
    // Typing "subtitle" pill — appears once the bot touches the ground and
    // starts walking toward camera (~25s into the 32s clip). Adjust this
    // single number if the exact moment should shift.
    typingPillText: 'AI does the labor. People do the living.',
    typingPillTriggerSeconds: 25,
  };

  const industryItems = [
    { name: 'Logistics', to: '/solutions/industries/logistics', iconPaths: (
      <>
        <path d="M3.4 8.4h9.8M3.4 12h7M3.4 15.6h9.8" />
        <path d="M15.4 12h5.2" />
        <path d="M18.1 9.3L20.8 12l-2.7 2.7" />
      </>
    ) },
    { name: 'Insurance', to: '/solutions/industries/insurance', iconPaths: (
      <>
        <path d="M12 3.6l6.6 6.6-6.6 6.6-6.6-6.6z" />
        <circle cx="12" cy="10.2" r="1.9" fill="currentColor" stroke="none" />
        <path d="M7.6 19.9h8.8" />
      </>
    ) },
    { name: 'Real Estate', to: '/solutions/industries/real-estate', discColor: '#CDEBEE', iconPaths: (
      <>
        <rect x="3.4" y="9.6" width="7.2" height="10.4" rx="1.8" />
        <rect x="13.4" y="4.4" width="7.2" height="15.6" rx="1.8" />
        <rect x="15.7" y="7.6" width="2.6" height="2.6" rx="0.7" fill="currentColor" stroke="none" />
      </>
    ) },
    { name: 'Retail', to: '/solutions/industries/retail', iconPaths: (
      <>
        <rect x="3.8" y="8.2" width="16.4" height="11.8" rx="3.2" />
        <path d="M8.5 8.2V6.8a3.5 3.5 0 017 0v1.4" />
        <path d="M8.6 12.4h6.8" />
      </>
    ) },
    { name: 'Healthcare', to: '/solutions/industries/healthcare', iconPaths: (
      <>
        <circle cx="12" cy="12" r="8.6" />
        <path d="M12 7.8v8.4M7.8 12h8.4" strokeWidth="2.1" />
      </>
    ) },
    { name: 'Finance', to: '/solutions/industries/finance', iconPaths: (
      <>
        <path d="M6.2 19.6v-5.2M12 19.6V9.4M17.8 19.6v-7.4" strokeWidth="2.1" />
        <circle cx="17.8" cy="6.6" r="1.7" fill="currentColor" stroke="none" />
      </>
    ) },
    { name: 'Manufacturing', to: '/solutions/industries/manufacturing', discColor: '#F6D3C3', iconPaths: (
      <>
        <path d="M12 3.3l7.4 4.3v8.8L12 20.7l-7.4-4.3V7.6z" />
        <circle cx="12" cy="12" r="3" />
      </>
    ) },
    { name: 'Government & Public Safety', to: '/solutions/industries/government', iconPaths: (
      <>
        <path d="M4.2 20h15.6" />
        <path d="M6.4 20v-8.6a5.6 5.6 0 0111.2 0V20" />
        <path d="M12 20v-5.6" />
      </>
    ) },
  ];
  // ============================================


  const caseStudies = [
    {
      company: 'Enterprise Alpha',
      industry: 'Technology',
      outcome: 'Scaling globally across regions and business units',
      quote: 'GoogLyn AI transformed how we operate at scale. The intelligence is truly elegant.',
      metrics: [
        { label: '40% faster', value: 'deployment' },
        { label: '60% reduction', value: 'in overhead' },
      ],
    },
    {
      company: 'Growth Company',
      industry: 'Operations',
      outcome: 'Boosting performance while reducing manual overhead',
      quote: 'The accessible luxury approach made AI adoption seamless for our entire team.',
      metrics: [
        { label: '3x productivity', value: 'increase' },
        { label: '50% cost', value: 'savings' },
      ],
    },
    {
      company: 'Innovation Corp',
      industry: 'Enterprise',
      outcome: 'Handling critical flows with full autonomy',
      quote: 'Elegant intelligence that just works. No complexity, just results.',
      metrics: [
        { label: '99.9% uptime', value: 'reliability' },
        { label: '2x revenue', value: 'growth' },
      ],
    },
  ];

  const scaleFeatures = [
    {
      title: 'Observable',
      description: 'Full visibility into behavioral and technical performance across the entire system.',
      icon: TrendingUp,
    },
    {
      title: 'Explainable',
      description: 'Not a black box — every decision and action can be audited in detail.',
      icon: CheckCircle2,
    },
    {
      title: 'Scalable',
      description: 'Guaranteed uptime with smart fallbacks and seamless scaling on enterprise infrastructure.',
      icon: Award,
    },
  ];

  return (
    <div className="w-full">
      {/* Pure Empty Hero - Just Video/Image Background */}
      <HeroMedia 
        type={heroSettings.type}
        videoId={heroSettings.videoId}
        videoUrl={heroSettings.videoUrl}
        cloudflareVideoId={heroSettings.cloudflareVideoId}
        imageUrl={heroSettings.imageUrl}
        overlay={heroSettings.overlay}
        useBottomLeftControls
        showTypingPill
        typingPillText={heroSettings.typingPillText}
        typingPillTriggerSeconds={heroSettings.typingPillTriggerSeconds}
        onBookDemo={() => setDemoOpen(true)}
      >
        <div className="min-h-[85vh]" data-testid="home-hero-section">
          {/* Completely empty - just shows your uploaded video/image */}
        </div>
      </HeroMedia>

      {/* Industry Ticker Strip — flat monochrome logo-strip style, continuous scroll, seamless loop */}
      <div
        className="industry-ticker w-full overflow-hidden py-6"
        data-testid="home-industry-ticker"
        aria-label="Industries we serve"
        style={{ backgroundColor: '#F5F0E8' }}
      >
        <div className="industry-ticker-track flex items-center whitespace-nowrap w-max">
          {[0, 1].map((setIdx) => (
            <div
              key={setIdx}
              className="flex items-center shrink-0"
              aria-hidden={setIdx === 1 ? 'true' : undefined}
            >
              {/* Start marker — small hollow/outlined dot */}
              <span
                className="inline-block w-2 h-2 rounded-full ml-10"
                style={{ border: '1px solid #666666', backgroundColor: 'transparent' }}
                aria-hidden="true"
                data-testid={setIdx === 0 ? 'home-industry-ticker-start-dot' : undefined}
              />
              {industryItems.map(({ name, iconPaths, to, discColor }, idx) => (
                <Link
                  key={`${setIdx}-${idx}`}
                  to={to}
                  className={`industry-item industry-disc-c${(idx % 4) + 1}`}
                  data-testid={setIdx === 0 ? `home-industry-ticker-item-${idx}` : `home-industry-ticker-item-${idx}-dup`}
                  aria-label={`${name} industry solutions`}
                >
                  <span
                    className="industry-disc"
                    style={{ animationDelay: `${idx * 0.3}s`, ...(discColor ? { backgroundColor: discColor } : {}) }}
                    aria-hidden="true"
                  >
                    <svg
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.6"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      {iconPaths}
                    </svg>
                  </span>
                  <span className="industry-label">{name}</span>
                </Link>
              ))}
              {/* End marker — small solid filled dot */}
              <span
                className="inline-block w-2 h-2 rounded-full mr-10"
                style={{ backgroundColor: '#666666' }}
                aria-hidden="true"
                data-testid={setIdx === 0 ? 'home-industry-ticker-end-dot' : undefined}
              />
            </div>
          ))}
        </div>
      </div>

      {/* Main Content Section - Moved from Hero - min-h-[85vh] to match hero */}
      <section className="min-h-[85vh] bg-[#F5F7F5] flex items-center justify-center">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 w-full py-16 md:py-28">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-10 items-center">
            <motion.div
              className="lg:col-span-7"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
            >
              <h1 
                className="text-4xl sm:text-5xl lg:text-6xl font-medium tracking-tight text-black/95 mb-6"
                data-testid="home-hero-headline"
              >
                Elegant Intelligence For{' '}
                <HeroRotatingText words={rotatingWords} />
              </h1>
              <p className="text-lg md:text-xl text-black/70 max-w-2xl mb-8 leading-relaxed">
                GoogLyn AI exists to bring elegant intelligence into everyday life — making technology feel intuitive, beautiful, and personal.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Button
                  size="lg"
                  onClick={() => setDemoOpen(true)}
                  className="bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] hover:brightness-105 transition-all text-base px-8"
                  data-testid="home-hero-book-demo-button"
                >
                  Book a demo
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
                <Button
                  size="lg"
                  variant="outline"
                  onClick={() => document.getElementById('case-studies')?.scrollIntoView({ behavior: 'smooth' })}
                  className="bg-transparent text-black border-black/20 hover:bg-black/5 text-base px-8"
                  data-testid="home-hero-view-case-studies-button"
                >
                  Hear From Our Clients
                </Button>
              </div>
            </motion.div>

            <motion.div
              className="lg:col-span-5"
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
            >
              <div className="grid grid-cols-1 gap-4">
                {heroSideCards.map((card, idx) => (
                  <Card
                    key={idx}
                    className="bg-white border-[#D4DDD1]/40 hover:bg-[#F5F7F5] hover:border-[#D4DDD1] transition-all duration-300"
                    data-testid={`home-hero-side-card-${idx}`}
                  >
                    <CardHeader>
                      <div className="flex items-start gap-4">
                        <div
                          className="flex items-center justify-center flex-shrink-0"
                          style={{ width: 52, height: 52, borderRadius: 12, backgroundColor: '#25365A' }}
                        >
                          <card.icon
                            style={{ width: 24, height: 24, color: '#D4AF37', strokeWidth: 1.6 }}
                          />
                        </div>
                        <div>
                          <CardTitle className="text-black/90 text-lg font-sans mb-1.5">{card.title}</CardTitle>
                          <p className="text-lg text-black/70 leading-relaxed">{card.description}</p>
                        </div>
                      </div>
                    </CardHeader>
                  </Card>
                ))}
              </div>
            </motion.div>
          </div>

          {/* Trust badges */}
        </div>
      </section>

      {/* White-Glove Excellence Section — For Enterprise */}
      <section
        className="min-h-[85vh] bg-[#E3E8E0] flex items-center justify-center overflow-x-hidden"
        data-testid="home-white-glove-section"
      >
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 w-full py-16 md:py-24">
          <motion.div
            className="text-center"
            style={{ marginBottom: '32px' }}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <div
              className="text-xs md:text-sm tracking-[0.2em] uppercase text-[#E8A0B8] mb-4 font-medium"
              data-testid="home-white-glove-eyebrow"
            >
              For Enterprise
            </div>
            <h2
              className="text-3xl md:text-4xl font-medium text-black/90 mb-4"
              data-testid="home-white-glove-headline"
            >
              White-Glove Excellence
            </h2>
            <p
              className="text-lg text-black/70 max-w-3xl mx-auto"
              data-testid="home-white-glove-subheadline"
            >
              Enterprise AI, handled the way your business expects — personal, precise, without compromise.
            </p>
          </motion.div>

          <div
            className="relative w-full"
            style={{ height: `${wgCardHeight + 60}px`, overflow: wgIsMobile ? 'visible' : 'hidden' }}
            onTouchStart={wgOnTouchStart}
            onTouchEnd={wgOnTouchEnd}
            data-testid="home-white-glove-carousel"
          >
            {whiteGloveCards.map((item, idx) => {
              const Icon = item.icon;
              const number = String(idx + 1).padStart(2, '0');
              return (
                <div
                  key={item.title}
                  style={wgGetCardStyle(idx)}
                  onClick={() => idx !== wgActive && setWgActive(idx)}
                  role="group"
                  aria-roledescription="slide"
                  aria-label={`${number} ${item.title}`}
                  data-testid={`home-white-glove-card-${idx}`}
                >
                  <div className="flex items-start justify-between">
                    <div
                      style={{
                        width: '52px',
                        height: '52px',
                        borderRadius: '14px',
                        border: '1px solid rgba(212,175,55,0.45)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                      }}
                    >
                      <Icon size={24} color="#D4AF37" strokeWidth={1.75} />
                    </div>
                    <div
                      style={{
                        fontSize: '46px',
                        fontWeight: 300,
                        color: '#D4AF37',
                        lineHeight: 1,
                      }}
                    >
                      {number}
                    </div>
                  </div>
                  <div
                    style={{
                      marginTop: '40px',
                      width: '44px',
                      height: '1px',
                      backgroundColor: '#D4AF37',
                    }}
                  />
                  <h3
                    style={{
                      marginTop: '26px',
                      fontSize: '27px',
                      color: '#EDE8DC',
                      fontWeight: 500,
                      lineHeight: 1.2,
                    }}
                  >
                    {item.title}
                  </h3>
                  <p
                    style={{
                      marginTop: '16px',
                      fontSize: '16px',
                      color: '#B9BFC9',
                      lineHeight: 1.65,
                    }}
                  >
                    {item.description}
                  </p>
                </div>
              );
            })}
          </div>

          {/* Carousel controls — arrows + dots */}
          <div
            className="flex items-center justify-center"
            style={{ gap: '22px', marginTop: '24px' }}
            data-testid="home-white-glove-controls"
          >
            <button
              type="button"
              onClick={wgGoPrev}
              aria-label="Previous slide"
              data-testid="home-white-glove-prev"
              className="group flex items-center justify-center rounded-full bg-transparent transition-colors duration-300 hover:bg-[#141E30]"
              style={{
                width: '46px',
                height: '46px',
                border: '1px solid rgba(20,30,48,0.45)',
              }}
            >
              <ChevronLeft
                size={18}
                className="text-[#141E30] group-hover:text-[#D4AF37] transition-colors duration-300"
              />
            </button>
            <div
              className="flex items-center"
              style={{ gap: '10px' }}
              role="tablist"
            >
              {whiteGloveCards.map((_, idx) => {
                const isActive = idx === wgActive;
                return (
                  <button
                    key={idx}
                    type="button"
                    onClick={() => setWgActive(idx)}
                    aria-label={`Go to slide ${idx + 1}`}
                    aria-selected={isActive}
                    role="tab"
                    data-testid={`home-white-glove-dot-${idx}`}
                    style={{
                      height: '8px',
                      width: isActive ? '30px' : '8px',
                      borderRadius: '9999px',
                      backgroundColor: isActive ? '#D4AF37' : 'rgba(20,30,48,0.25)',
                      border: 'none',
                      padding: 0,
                      cursor: 'pointer',
                      transition: `width 0.6s ${wgEasing}, background-color 0.6s ${wgEasing}`,
                    }}
                  />
                );
              })}
            </div>
            <button
              type="button"
              onClick={wgGoNext}
              aria-label="Next slide"
              data-testid="home-white-glove-next"
              className="group flex items-center justify-center rounded-full bg-transparent transition-colors duration-300 hover:bg-[#141E30]"
              style={{
                width: '46px',
                height: '46px',
                border: '1px solid rgba(20,30,48,0.45)',
              }}
            >
              <ChevronRight
                size={18}
                className="text-[#141E30] group-hover:text-[#D4AF37] transition-colors duration-300"
              />
            </button>
          </div>
        </div>
      </section>

      {/* Elegance That Delivers — Brands Strip */}
      <section
        id="case-studies"
        className="relative overflow-hidden bg-[#F5F7F5] py-16 md:py-24"
        data-testid="elegance-brands-section"
      >
        {/* Decorative gold curves (purely visual) */}
        <div className="pointer-events-none absolute inset-0 overflow-hidden" aria-hidden="true">
          <svg
            className="absolute inset-0 w-full h-full"
            viewBox="0 0 1600 900"
            preserveAspectRatio="none"
            fill="none"
          >
            <path
              d="M -100 180 C 300 60, 900 320, 1700 120"
              stroke="#B9942C"
              strokeOpacity="0.22"
              strokeWidth="1"
              fill="none"
            />
            <path
              d="M -100 760 C 400 620, 1000 880, 1700 640"
              stroke="#B9942C"
              strokeOpacity="0.22"
              strokeWidth="1"
              fill="none"
            />
            <path
              d="M 1800 -80 C 1300 260, 1500 620, 900 900"
              stroke="#B9942C"
              strokeOpacity="0.22"
              strokeWidth="1"
              fill="none"
            />
            <path
              d="M -200 500 C 300 420, 700 540, 1700 460"
              stroke="#B9942C"
              strokeOpacity="0.22"
              strokeWidth="1"
              fill="none"
            />
          </svg>
        </div>

        {/* Top headings */}
        <div className="relative z-10 mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2
              className="elegance-h1"
              style={{ color: '#141E30', fontWeight: 300, lineHeight: 1.1, margin: 0 }}
              data-testid="elegance-heading"
            >
              Elegance That Delivers
            </h2>
            <p
              className="elegance-sub"
              style={{ color: '#3A4252', margin: '16px auto 0', maxWidth: '820px' }}
            >
              Trusted by those who believe intelligent technology should feel as good as it performs.
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.05 }}
            className="elegance-brands-heading-wrap"
          >
            <h3
              className="elegance-h2"
              style={{ color: '#141E30', fontWeight: 300, lineHeight: 1.1, margin: 0 }}
            >
              Brands we've <span style={{ color: '#B9942C' }}>brought to life</span>
            </h3>
            <p
              className="elegance-para"
              style={{
                color: '#3A4252',
                lineHeight: 1.6,
                maxWidth: '860px',
                margin: '20px auto 0',
              }}
            >
              Every commercial begins with a brand and a story worth telling. These are some of the worlds we've created — entirely through AI.
            </p>
          </motion.div>
        </div>

        {/* Brand strip bar */}
        <div
          className="relative z-10 flex justify-center elegance-bar-wrap"
          style={{ marginTop: '64px' }}
        >
          <div
            className="elegance-brand-bar"
            data-testid="elegance-brand-bar"
          >
            {/* Left/right edge fades */}
            <div className="elegance-fade elegance-fade-left" aria-hidden="true" />
            <div className="elegance-fade elegance-fade-right" aria-hidden="true" />

            <div className="elegance-viewport">
              <div className="elegance-track" data-testid="elegance-brand-track">
                {[
                  { name: 'VELDONO', initials: 'VD' },
                  { name: 'The 6AM Co.', initials: '6A' },
                  { name: 'Vacation Peace', initials: 'VP' },
                  { name: 'Pase', initials: 'PA' },
                  { name: 'Fromagerie Lumière', initials: 'FL' },
                  { name: 'Lumière X', initials: 'LX' },
                  { name: 'Altessa', initials: 'AL' },
                  { name: 'ARQEN', initials: 'AR' },
                  { name: 'Velvet Frost', initials: 'VF' },
                  { name: 'Blessed Nacter', initials: 'BN' },
                  { name: 'Dragon Rise', initials: 'DR' },
                ]
                  .concat([
                    { name: 'VELDONO', initials: 'VD' },
                    { name: 'The 6AM Co.', initials: '6A' },
                    { name: 'Vacation Peace', initials: 'VP' },
                    { name: 'Pase', initials: 'PA' },
                    { name: 'Fromagerie Lumière', initials: 'FL' },
                    { name: 'Lumière X', initials: 'LX' },
                    { name: 'Altessa', initials: 'AL' },
                    { name: 'ARQEN', initials: 'AR' },
                    { name: 'Velvet Frost', initials: 'VF' },
                    { name: 'Blessed Nacter', initials: 'BN' },
                    { name: 'Dragon Rise', initials: 'DR' },
                  ])
                  .map((brand, idx) => (
                    <div
                      key={idx}
                      className="elegance-brand-item"
                      tabIndex={0}
                      aria-label={brand.name}
                      data-testid={`elegance-brand-${idx}`}
                    >
                      <span className="elegance-ring" aria-hidden="true">
                        <span className="elegance-inner">{brand.initials}</span>
                      </span>
                      <span className="elegance-name">{brand.name}</span>
                    </div>
                  ))}
              </div>
            </div>
          </div>
        </div>

        <style>{`
          .elegance-h1 { font-size: 50px; }
          .elegance-sub { font-size: 20px; }
          .elegance-brands-heading-wrap { margin-top: 76px; }
          .elegance-h2 { font-size: 44px; }
          .elegance-para { font-size: 19px; }

          .elegance-brand-bar {
            position: relative;
            width: 1240px;
            max-width: calc(100% - 48px);
            height: 104px;
            border-radius: 18px;
            background: #ffffff;
            border: 1px solid rgba(20,30,48,0.08);
            box-shadow: 0 6px 24px rgba(20,30,48,0.06);
          }
          .elegance-viewport {
            height: 100%;
            width: 100%;
            overflow: hidden;
            /* Allow items to overflow vertically on hover without being clipped,
               while still clipping horizontally for the scrolling track. */
            clip-path: inset(-200px 0 -200px 0);
            -webkit-clip-path: inset(-200px 0 -200px 0);
            border-radius: 18px;
          }
          .elegance-track {
            display: flex;
            align-items: center;
            height: 100%;
            width: max-content;
            animation: elegance-brand-scroll 46s linear infinite;
            padding-left: 34px;
          }
          .elegance-brand-bar:hover .elegance-track {
            animation-play-state: paused;
          }
          .elegance-brand-item {
            display: flex;
            align-items: center;
            gap: 12px;
            margin-right: 34px;
            flex-shrink: 0;
            cursor: default;
            outline: none;
            transition: transform 0.35s ease;
            transform-origin: center center;
            position: relative;
          }
          .elegance-brand-item:hover,
          .elegance-brand-item:focus-visible,
          .elegance-brand-item:active {
            transform: scale(1.25);
            z-index: 5;
          }
          .elegance-ring {
            display: inline-flex;
            align-items: center;
            justify-content: center;
            width: 62px;
            height: 62px;
            border-radius: 50%;
            border: 2px solid #B9942C;
            padding: 3px;
            box-sizing: border-box;
            flex-shrink: 0;
            transition: border-color 0.35s ease, box-shadow 0.35s ease;
          }
          .elegance-brand-item:hover .elegance-ring,
          .elegance-brand-item:focus-visible .elegance-ring,
          .elegance-brand-item:active .elegance-ring {
            border-color: #D4AF37;
            box-shadow: 0 0 24px rgba(212,175,55,0.55);
          }
          .elegance-inner {
            display: inline-flex;
            align-items: center;
            justify-content: center;
            width: 100%;
            height: 100%;
            border-radius: 50%;
            background: #25365A;
            color: #D4AF37;
            font-weight: 700;
            font-size: 15px;
            line-height: 1;
            letter-spacing: 0.02em;
          }
          .elegance-name {
            font-size: 15px;
            color: #141E30;
            max-width: 104px;
            line-height: 1.25;
            white-space: normal;
            word-break: normal;
            overflow-wrap: break-word;
          }
          .elegance-fade {
            position: absolute;
            top: 0;
            bottom: 0;
            width: 72px;
            z-index: 3;
            pointer-events: none;
          }
          .elegance-fade-left {
            left: 0;
            background: linear-gradient(to right, #ffffff 0%, rgba(255,255,255,0) 100%);
            border-top-left-radius: 18px;
            border-bottom-left-radius: 18px;
          }
          .elegance-fade-right {
            right: 0;
            background: linear-gradient(to left, #ffffff 0%, rgba(255,255,255,0) 100%);
            border-top-right-radius: 18px;
            border-bottom-right-radius: 18px;
          }
          @keyframes elegance-brand-scroll {
            from { transform: translateX(0); }
            to   { transform: translateX(-50%); }
          }
          @media (max-width: 767px) {
            .elegance-h1 { font-size: 32px; }
            .elegance-sub { font-size: 16px; }
            .elegance-brands-heading-wrap { margin-top: 56px; }
            .elegance-h2 { font-size: 28px; }
            .elegance-para { font-size: 15px; }
            .elegance-bar-wrap { margin-top: 48px !important; }
            .elegance-brand-bar {
              width: calc(100% - 48px);
              max-width: calc(100% - 48px);
            }
          }
          @media (prefers-reduced-motion: reduce) {
            .elegance-track { animation: none; }
            .elegance-brand-item { transition: none; }
          }
        `}</style>
      </section>

      {/* Scale Features Section - min-h-[85vh] to match hero */}
      <section className="min-h-[85vh] bg-white flex items-center justify-center">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 w-full py-16 md:py-24">
          <motion.div
            className="text-center mb-10 md:mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl md:text-4xl font-medium text-black/90 mb-4">
              Designed for scale
            </h2>
            <p className="text-lg text-black/70 max-w-2xl mx-auto">
              Enterprise infrastructure that works reliably at scale — observable and explainable.
            </p>
          </motion.div>

          {/* Performance Graph */}
          <motion.div
            className="mb-10 md:mb-16 max-w-4xl mx-auto"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
          >
            <div className="bg-gradient-to-br from-[#E8E4DC] to-[#F5F7F5] rounded-2xl p-5 sm:p-8 border border-[#D4DDD1]/30">
              <div className="mb-6">
                <h3 className="text-lg sm:text-xl font-medium text-black/90 mb-2">Performance at Scale</h3>
                <p className="text-sm text-black/60">System uptime and response efficiency over time</p>
              </div>
              
              {/* Graph Container */}
              <div className="relative h-56 sm:h-64 bg-white/50 rounded-xl p-4 sm:p-6 border border-[#D4DDD1]/20">
                {/* Y-axis labels */}
                <div className="absolute left-0 top-0 bottom-0 flex flex-col justify-between text-xs text-black/40 pr-2">
                  <span>100%</span>
                  <span>75%</span>
                  <span>50%</span>
                  <span>25%</span>
                  <span>0%</span>
                </div>
                
                {/* Graph area */}
                <div className="ml-8 h-full relative">
                  {/* Grid lines */}
                  <div className="absolute inset-0 flex flex-col justify-between">
                    {[0, 1, 2, 3, 4].map((i) => (
                      <div key={i} className="border-t border-black/5" />
                    ))}
                  </div>
                  
                  {/* SVG Graph */}
                  <svg className="absolute inset-0 w-full h-full" viewBox="0 0 100 100" preserveAspectRatio="none">
                    {/* Area fill gradient */}
                    <defs>
                      <linearGradient id="areaGradient" x1="0%" y1="0%" x2="0%" y2="100%">
                        <stop offset="0%" style={{ stopColor: '#E8A0B8', stopOpacity: 0.3 }} />
                        <stop offset="100%" style={{ stopColor: '#E8A0B8', stopOpacity: 0.05 }} />
                      </linearGradient>
                    </defs>
                    
                    {/* Area under curve */}
                    <path
                      d="M 0,80 L 10,70 L 20,65 L 30,55 L 40,45 L 50,35 L 60,28 L 70,22 L 80,18 L 90,12 L 100,8 L 100,100 L 0,100 Z"
                      fill="url(#areaGradient)"
                    />
                    
                    {/* Main line */}
                    <path
                      d="M 0,80 L 10,70 L 20,65 L 30,55 L 40,45 L 50,35 L 60,28 L 70,22 L 80,18 L 90,12 L 100,8"
                      fill="none"
                      stroke="#E8A0B8"
                      strokeWidth="0.5"
                      vectorEffect="non-scaling-stroke"
                    />
                    
                    {/* Data points */}
                    {[
                      { x: 0, y: 80 }, { x: 10, y: 70 }, { x: 20, y: 65 }, { x: 30, y: 55 },
                      { x: 40, y: 45 }, { x: 50, y: 35 }, { x: 60, y: 28 }, { x: 70, y: 22 },
                      { x: 80, y: 18 }, { x: 90, y: 12 }, { x: 100, y: 8 }
                    ].map((point, idx) => (
                      <circle
                        key={idx}
                        cx={point.x}
                        cy={point.y}
                        r="0.8"
                        fill="#E8A0B8"
                        vectorEffect="non-scaling-stroke"
                      />
                    ))}
                  </svg>
                </div>
                
                {/* X-axis labels */}
                <div className="ml-8 mt-2 flex justify-between text-xs text-black/40">
                  <span>Q1</span>
                  <span>Q2</span>
                  <span>Q3</span>
                  <span>Q4</span>
                </div>
              </div>
              
              {/* Stats */}
              <div className="grid grid-cols-3 gap-2 sm:gap-4 mt-6">
                <div className="text-center">
                  <div className="text-xl sm:text-2xl font-medium text-[#E8A0B8]">99.9%</div>
                  <div className="text-xs text-black/60 mt-1">Uptime</div>
                </div>
                <div className="text-center">
                  <div className="text-xl sm:text-2xl font-medium text-[#E8A0B8]">&lt;50ms</div>
                  <div className="text-xs text-black/60 mt-1">Response Time</div>
                </div>
                <div className="text-center">
                  <div className="text-xl sm:text-2xl font-medium text-[#E8A0B8]">10M+</div>
                  <div className="text-xs text-black/60 mt-1">Requests/Day</div>
                </div>
              </div>
            </div>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {scaleFeatures.map((feature, idx) => (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: idx * 0.1 }}
              >
                <Card className="h-full bg-[#E8E4DC] border-[#F4C2D4]/30 hover:bg-[#FDF7F9] hover:border-[#E8A0B8]/50 hover:-translate-y-1 transition-all duration-300 text-center">
                  <CardHeader>
                    <div className="w-14 h-14 rounded-xl bg-[#E8A0B8]/10 flex items-center justify-center mx-auto mb-4">
                      <feature.icon className="h-7 w-7 text-[#E8A0B8]" />
                    </div>
                    <CardTitle className="text-black/90 text-xl font-medium mb-2">
                      {feature.title}
                    </CardTitle>
                    <CardDescription className="text-black/70 text-sm leading-relaxed">
                      {feature.description}
                    </CardDescription>
                  </CardHeader>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <DemoModal open={demoOpen} onOpenChange={setDemoOpen} />
    </div>
  );
};

export default Home;
